/**
 * @fileoverview Product Analytics - Custom Event Tracking
 * @module features/analytics/events
 * 
 * @description
 * Track custom events with flexible properties for any user action
 * or system event. Supports categorization, value tracking, and batching.
 * 
 * @useCases
 * - Track button clicks and CTA interactions
 * - Monitor feature usage and engagement
 * - Capture form submissions and errors
 * - Log search queries and results
 * - Record video/audio playback events
 * 
 * @metrics
 * - Event counts by type
 * - Event trends over time
 * - Unique users per event
 * - Event value aggregations
 * - Event-to-conversion correlation
 * 
 * @chartData
 * - Bar chart: Top events by count
 * - Line chart: Event trends over time
 * - Heatmap: Events by hour/day
 * - Sankey: Event flow paths
 * 
 * @example
 * ```typescript
 * const events = new Events(webId, endpoint, apiKey);
 * 
 * // Track a button click
 * await events.track({
 *   eventName: 'cta_clicked',
 *   category: 'engagement',
 *   action: 'click',
 *   label: 'signup_hero'
 * });
 * 
 * // Track with value
 * await events.track({
 *   eventName: 'video_watched',
 *   value: 120, // seconds
 *   properties: { videoId: 'intro-video' }
 * });
 * ```
 */

import { checkCaptureName } from "./captureNames";
import { FlowGridTransport } from "../../client/transport";
import { 
  CustomEvent,
  ClickEvent,
  FormEvent,
  SearchEvent,
  ErrorEvent,
  TimeSeriesData,
  DateRangeFilter,
  PaginationParams,
  ChartDataPoint
} from "../../types";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Configuration for tracking a custom event.
 */
export interface EventTrackConfig {
  /** Event name (required) */
  eventName: string;
  /** Event category for grouping */
  category?: string;
  /** Event action */
  action?: string;
  /** Event label for sub-categorization */
  label?: string;
  /** Numeric value associated with event */
  value?: number;
  /** Non-interactive event (won't affect bounce rate) */
  nonInteraction?: boolean;
  /** User ID */
  userId?: string;
  /** Session ID */
  sessionId?: string;
  /** Custom properties */
  properties?: Record<string, unknown>;
}

/**
 * Batch event tracking configuration.
 */
export interface BatchEventConfig {
  /** Array of events to track */
  events: EventTrackConfig[];
}

/**
 * Event response from API.
 */
export interface EventResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    eventId: string;
    tracked: boolean;
  };
  timestamp: string;
}

/**
 * Batch event response.
 */
export interface BatchEventResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    tracked: number;
    failed: number;
    eventIds: string[];
  };
  timestamp: string;
}

/**
 * Event analytics data for charts.
 */
export interface EventAnalyticsData {
  /** Top events by count */
  topEvents: Array<{
    eventName: string;
    category: string;
    count: number;
    uniqueUsers: number;
    totalValue: number;
  }>;
  /** Events time series */
  timeSeries: TimeSeriesData;
  /** Events by category */
  byCategory: Array<{
    category: string;
    count: number;
    percentage: number;
  }>;
  /** Summary metrics */
  summary: {
    totalEvents: number;
    uniqueEventTypes: number;
    eventsPerUser: number;
    totalValue: number;
  };
}

// ============================================================================
// EVENTS CLASS
// ============================================================================

/**
 * Events - Track and analyze custom events.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides flexible custom event tracking including:
 * - Standard event tracking with categories
 * - Click and interaction tracking
 * - Form submission tracking
 * - Search event tracking
 * - Error and exception logging
 * - Batch event submission
 * 
 * @example
 * ```typescript
 * const events = new Events(webId, endpoint, apiKey);
 * 
 * // Track feature usage
 * await events.track({
 *   eventName: 'feature_used',
 *   category: 'features',
 *   action: 'use',
 *   label: 'export_pdf',
 *   properties: { format: 'A4', pages: 5 }
 * });
 * ```
 */
const warned = new Set<string>();

/** One console line per distinct problem — a micro-interaction can fire hundreds of times. */
function warnOnce(message: string): void {
  if (warned.has(message)) return;
  warned.add(message);
  console.warn(`[Flowgrid] ${message}`);
}

export class Events extends FlowGridTransport {
  /**
   * Capture one of your own events, stored under exactly this name — so it
   * shows up by name in the dashboard, in flag targeting ("Users who did
   * something") and in its own metrics. Built for micro-interactions: fire
   * and forget, as often as they happen.
   *
   * Never throws and never rejects. A name Flowgrid uses itself, or an
   * invalid payload, drops the event with one console warning.
   *
   * @example
   * ```typescript
   * fg.capture('filter_applied', { filter: 'price', value: 'under-50' });
   * ```
   */
  async capture(name: string, properties: Record<string, unknown> = {}): Promise<void> {
    const check = checkCaptureName(name);
    if (!check.ok) {
      warnOnce(`capture() skipped: ${check.reason}.`);
      return;
    }

    try {
      await this.init(check.name, {
        ...properties,
        type: "custom_event",
        eventName: check.name,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      warnOnce(`capture("${check.name}") was not sent: ${(error as Error)?.message ?? "unknown error"}.`);
    }
  }

  /**
   * Tracks a custom event.
   * 
   * @param config - Event configuration
   * @returns Promise resolving to event response
   * 
   * @example
   * ```typescript
   * await events.track({
   *   eventName: 'upgrade_clicked',
   *   category: 'monetization',
   *   action: 'click',
   *   label: 'pricing_page',
   *   value: 99.99
   * });
   * ```
   */
  async track(config: EventTrackConfig): Promise<EventResponse> {
    return await this.init("custom_event", {
      type: "custom_event",
      eventName: config.eventName,
      category: config.category || "general",
      action: config.action,
      label: config.label,
      value: config.value,
      nonInteraction: config.nonInteraction || false,
      userId: config.userId,
      sessionId: config.sessionId,
      ...config.properties,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks multiple events in a single request.
   * 
   * @param config - Batch configuration with events array
   * @returns Promise resolving to batch response
   * 
   * @example
   * ```typescript
   * await events.trackBatch({
   *   events: [
   *     { eventName: 'step_1_complete', category: 'onboarding' },
   *     { eventName: 'step_2_complete', category: 'onboarding' },
   *     { eventName: 'step_3_complete', category: 'onboarding' }
   *   ]
   * });
   * ```
   */
  async trackBatch(config: BatchEventConfig): Promise<BatchEventResponse> {
    const events = config.events.map(e => ({
      ...e,
      timestamp: new Date().toISOString()
    }));

    return await this.init("batch_events", {
      type: "batch_events",
      events,
      count: events.length,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a click/interaction event.
   * 
   * @param config - Click event configuration
   * @returns Promise resolving to event response
   * 
   * @example
   * ```typescript
   * document.addEventListener('click', (e) => {
   *   if (e.target.dataset.track) {
   *     events.trackClick({
   *       elementId: e.target.id,
   *       elementText: e.target.textContent,
   *       targetUrl: e.target.href
   *     });
   *   }
   * });
   * ```
   */
  async trackClick(config: ClickEvent): Promise<EventResponse> {
    return await this.init("click_event", {
      type: "click_event",
      ...config,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a form submission event.
   * 
   * @param config - Form event configuration
   * @returns Promise resolving to event response
   * 
   * @example
   * ```typescript
   * form.addEventListener('submit', async (e) => {
   *   await events.trackForm({
   *     formId: 'contact-form',
   *     formName: 'Contact Us',
   *     success: true,
   *     fields: ['name', 'email', 'message'],
   *     completionTime: 45
   *   });
   * });
   * ```
   */
  async trackForm(config: FormEvent): Promise<EventResponse> {
    return await this.init("form_event", {
      type: "form_event",
      ...config,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks a search event.
   * 
   * @param config - Search event configuration
   * @returns Promise resolving to event response
   * 
   * @example
   * ```typescript
   * await events.trackSearch({
   *   query: 'wireless headphones',
   *   category: 'products',
   *   resultsCount: 42,
   *   filters: { brand: 'Sony', priceRange: '100-200' }
   * });
   * ```
   */
  async trackSearch(config: SearchEvent): Promise<EventResponse> {
    return await this.init("search_event", {
      type: "search_event",
      query: config.query,
      category: config.category,
      resultsCount: config.resultsCount,
      resultClicked: config.resultClicked,
      clickedPosition: config.clickedPosition,
      filters: config.filters,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks an error or exception.
   * 
   * @param config - Error event configuration
   * @returns Promise resolving to event response
   * 
   * @example
   * ```typescript
   * window.onerror = (message, source, line, col, error) => {
   *   events.trackError({
   *     message: message.toString(),
   *     stack: error?.stack,
   *     type: error?.name,
   *     fatal: false,
   *     context: source
   *   });
   * };
   * ```
   */
  async trackError(config: ErrorEvent): Promise<EventResponse> {
    return await this.init("error_event", {
      type: "error_event",
      message: config.message,
      stack: config.stack,
      errorType: config.type,
      fatal: config.fatal || false,
      context: config.context,
      userAction: config.userAction,
      timestamp: new Date().toISOString()
    });
  }

}

export default Events;
